import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Button, Col, Container, Modal, Row, Stack } from 'react-bootstrap';
import { truncateText } from 'cb-unified';
import { displayDateTimeToMinute, displayLocaleTime, getListRowLink } from 'gossamer-unified';
import { BasicFormBlock, fetchApi, makeResetCache, notifyError, notifyResponse, Uneditable } from 'gossamer-front-end';
import {
  AllBookSingleEventRequest,
  BookingView,
  BookingExtendedView,
  Event,
  GroupBookingComputedStatusOptions,
  UserView,
} from 'cb-model';
import { getBookingMatrixStatus } from './InstitutionGroupBookingMatrixUtils';
import { UnbookUserFromEventButton } from '../user/UnbookUserFromEventButton';
import classes from './BookingModal.module.css';

interface BookingModalProps {
  event_id: number;
  event_name: string;
  visibility_limit: string;
  flow_option: string;
  user_id: string;
  user_name?: string;
  ticket_booking_id: number;
  event_added_at?: string;
  member_added_at?: string;
  start_at: string | null;
  end_at?: string | null;
  booking_status: string | null;
  booking_created_at?: string | null;
  ticket_title?: string;
  attendance_id: number | null;
  attendance_result: string | null;
  event_feedback_id: number | null;
  context: 'event' | 'user' | 'none';
  show: boolean;
  setShow: (val: boolean) => void;
  refresh?: () => void;
}

export const BookingModal = (props: BookingModalProps): React.JSX.Element => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const resetCache = makeResetCache(dispatch);
  const titleParts = [];
  if (props.context !== 'event') {
    titleParts.push(props.event_name);
  }
  if (props.context !== 'user' && props.user_name) {
    titleParts.push(props.user_name);
  }
  const title = titleParts.map((part) => truncateText(part, 60 / titleParts.length)).join(' / ');
  const status = getBookingMatrixStatus(props);
  const option = GroupBookingComputedStatusOptions.find((s) => s.id === status);
  const isBooked = props.booking_status === 'active';
  const canUnbook = isBooked && !props.attendance_id && !props.event_feedback_id;

  const bookUserToEvent = async () => {
    try {
      const payload: AllBookSingleEventRequest = {
        userId: props.user_id,
        eventId: props.event_id,
      };
      const response = await fetchApi('POST', `/main/book-user-to-event`, payload);
      resetCache(BookingView.id);
      resetCache(BookingExtendedView.id);
      notifyResponse(response);
      props.refresh?.();
      props.setShow(false);
    } catch (e) {
      notifyError(e, 'An error occurred while booking the member to the event');
    }
  };

  return (
    <Modal show={props.show} onHide={() => props.setShow(false)} centered backdrop="static">
      <Modal.Header closeButton={true}>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Container>
          <Row>
            <Col>
              <BasicFormBlock label="Status">
                <div>
                  <span
                    className={classes.BookingStatus}
                    style={{ backgroundColor: option?.bgColor, color: option?.textColor }}
                  >
                    {option?.cellLabel}
                  </span>
                  <b>{status}</b>
                </div>
              </BasicFormBlock>
            </Col>
          </Row>
          <Row>
            {!!props.start_at && (
              <Col>
                <BasicFormBlock label="Event Starts At">
                  <div>
                    <b>{displayDateTimeToMinute(props.start_at)}</b>
                    {!!props.end_at && (
                      <span>
                        {' '}
                        - <b>{displayLocaleTime(props.end_at)}</b>
                      </span>
                    )}
                  </div>
                </BasicFormBlock>
              </Col>
            )}
            {!!props.booking_created_at && (
              <Col>
                <BasicFormBlock label="Booked At">
                  <b>{displayDateTimeToMinute(props.booking_created_at)}</b>
                </BasicFormBlock>
              </Col>
            )}
          </Row>
          {!!props.visibility_limit && (
            <Row>
              <Col>
                <BasicFormBlock label="Visibility Limit">
                  <Uneditable field={Event.fields.visibility_limit} value={props.visibility_limit} />
                </BasicFormBlock>
              </Col>
            </Row>
          )}
          {!!props.flow_option && (
            <Row>
              <Col>
                <BasicFormBlock label="Flow Option">
                  <Uneditable field={Event.fields.flow_option} value={props.flow_option} />
                </BasicFormBlock>
              </Col>
            </Row>
          )}
          {!!props.ticket_title && (
            <Row>
              <Col>
                <BasicFormBlock label="Ticket Title">
                  <b>{props.ticket_title}</b>
                </BasicFormBlock>
              </Col>
            </Row>
          )}
          <Row>
            {props.context !== 'event' && (
              <Col>
                <NavLink to={getListRowLink(Event.listRowLink, String(props.event_id))}>
                  <b>Go to Event</b>
                </NavLink>
              </Col>
            )}
            {props.context !== 'user' && (
              <Col>
                <NavLink to={getListRowLink(UserView.listRowLink, String(props.user_id))}>
                  <b>Go to User</b>
                </NavLink>
              </Col>
            )}
          </Row>
          <Row>
            <Col>
              <Stack direction="horizontal" gap={3}>
                {canUnbook && (
                  <UnbookUserFromEventButton
                    value={String(props.ticket_booking_id)}
                    handleSuccess={() => {
                      props.setShow(false);
                      props.refresh?.();
                    }}
                  />
                )}
                {isBooked && !props.attendance_id && (
                  <Button
                    variant="warning"
                    size="sm"
                    onClick={() => navigate(`/admin/attendance/create/${props.ticket_booking_id}`)}
                  >
                    Create an Attendance Record
                  </Button>
                )}
                {isBooked && !!props.attendance_id && (
                  <Button
                    variant="warning"
                    size="sm"
                    onClick={() => navigate(`/admin/attendance/update/${props.attendance_id}`)}
                  >
                    Update the Attendance Record
                  </Button>
                )}
                {!isBooked && (
                  <Button variant="primary" size="sm" onClick={bookUserToEvent}>
                    Book User for Event
                  </Button>
                )}
              </Stack>
            </Col>
          </Row>
        </Container>
      </Modal.Body>
    </Modal>
  );
};
